import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { RequirePermission } from './contexts/PermissionContext';
import { SentryRouteStateBridge } from './observability/sentry';
import OperationalTabGate from './components/OperationalTabGate';

// Layout
import MainLayout from './layouts/MainLayout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CampaignListPage from './pages/CampaignListPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import CampaignCreatePage from './pages/CampaignCreatePage';
import ContactDetailPage from './pages/ContactDetailPage';
import ScriptListPage from './pages/ScriptListPage';
import ScriptCreatePage from './pages/ScriptCreatePage';
import CallsPage from './pages/CallsPage';
import CallDetailPage from './pages/CallDetailPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import AccountPage from './pages/AccountPage';
import SubscriptionPage from './pages/SubscriptionPage';
import UsersPage from './pages/UsersPage';
import UserFormPage from './pages/UserFormPage';
import AiAgentEndpointListPage from './pages/AiAgentEndpointListPage';
import AiAgentEndpointFormPage from './pages/AiAgentEndpointFormPage';
import ProviderListPage from './pages/ProviderListPage';
import ProviderFormPage from './pages/ProviderFormPage';
import AgentListPage from './pages/AgentListPage';
import AgentFormPage from './pages/AgentFormPage';
import SipTrunkListPage from './pages/SipTrunkListPage';
import SipTrunkFormPage from './pages/SipTrunkFormPage';
import GroupsPage from './pages/GroupsPage';
import GroupFormPage from './pages/GroupFormPage';
import TenantsPage from './pages/TenantsPage';
import TenantFormPage from './pages/TenantFormPage';
import PlansPage from './pages/PlansPage';
import PlanFormPage from './pages/PlanFormPage';
import AddonsPage from './pages/AddonsPage';
import AddonFormPage from './pages/AddonFormPage';
import MinutePacksAdminPage from './pages/MinutePacksAdminPage';
import MinutePackFormPage from './pages/MinutePackFormPage';
import TransferGroupsPage from './pages/TransferGroupsPage';
import TransferGroupFormPage from './pages/TransferGroupFormPage';
import AppointmentCalendarListPage from './pages/AppointmentCalendarListPage';
import AppointmentCalendarFormPage from './pages/AppointmentCalendarFormPage';
import IntegrationsHubPage from './pages/IntegrationsHubPage';
import ContactZPage from './pages/ContactZPage';
import HelpPage from './pages/HelpPage';
import HelpArticlePage from './pages/HelpArticlePage';

const CampaignAnalyticsPage = lazy(() => import('./pages/CampaignAnalyticsPage'));

// Protected Route
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const redirect = `/login?returnTo=${encodeURIComponent(returnTo)}`;
    return <Navigate to={redirect} replace />;
  }

  return <>{children}</>;
};

const PermissionRoute = ({
  permission,
  fallback = '/',
  children,
}: {
  permission: string | string[];
  fallback?: string;
  children: React.ReactNode;
}) => (
  <RequirePermission permission={permission} fallback={<Navigate to={fallback} replace />}>
    {children}
  </RequirePermission>
);

const PublicApiEntitlementRoute = ({ children }: { children: React.ReactNode }) => {
  const { capabilities, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!capabilities?.hasPublicApiEnabled) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const lazyFallback = (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
      <CircularProgress />
    </Box>
  );

  return (
    <>
      <SentryRouteStateBridge />
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route path="/" element={
          <ProtectedRoute>
            <OperationalTabGate>
              <MainLayout />
            </OperationalTabGate>
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="campaigns" element={<PermissionRoute permission="campaigns_access"><CampaignListPage /></PermissionRoute>} />
          <Route path="campaigns/new" element={<PermissionRoute permission="campaigns_access"><CampaignCreatePage /></PermissionRoute>} />
          <Route path="campaigns/:id" element={<PermissionRoute permission="campaigns_access"><CampaignDetailPage /></PermissionRoute>} />
          <Route
            path="campaigns/:id/analytics"
            element={(
              <PermissionRoute permission="campaigns_access">
                <Suspense fallback={lazyFallback}>
                  <CampaignAnalyticsPage />
                </Suspense>
              </PermissionRoute>
            )}
          />
          <Route path="campaigns/:campaignId/contacts/:contactId" element={<PermissionRoute permission="campaigns_access"><ContactDetailPage /></PermissionRoute>} />
          <Route path="scripts" element={<PermissionRoute permission="scripts_access"><ScriptListPage /></PermissionRoute>} />
          <Route path="scripts/new" element={<PermissionRoute permission="scripts_access"><ScriptCreatePage /></PermissionRoute>} />
          <Route path="scripts/:id" element={<PermissionRoute permission="scripts_access"><ScriptCreatePage /></PermissionRoute>} />
          <Route path="calls" element={<PermissionRoute permission="calls_access"><CallsPage /></PermissionRoute>} />
          <Route path="calls/:callId" element={<PermissionRoute permission="calls_access"><CallDetailPage /></PermissionRoute>} />
          <Route path="reports" element={<PermissionRoute permission={['campaigns_access', 'calls_reports_access', 'users_read']}><ReportsPage /></PermissionRoute>} />
          <Route path="ai-agent-endpoints" element={<PermissionRoute permission="ai_agent_endpoints_access"><AiAgentEndpointListPage /></PermissionRoute>} />
          <Route path="ai-agent-endpoints/new" element={<PermissionRoute permission="ai_agent_endpoints_access"><AiAgentEndpointFormPage /></PermissionRoute>} />
          <Route path="ai-agent-endpoints/:id" element={<PermissionRoute permission="ai_agent_endpoints_access"><AiAgentEndpointFormPage /></PermissionRoute>} />
          <Route path="sip-trunks" element={<PermissionRoute permission="sip_trunks_access"><SipTrunkListPage /></PermissionRoute>} />
          <Route path="sip-trunks/new" element={<PermissionRoute permission="sip_trunks_access"><SipTrunkFormPage /></PermissionRoute>} />
          <Route path="sip-trunks/:id" element={<PermissionRoute permission="sip_trunks_access"><SipTrunkFormPage /></PermissionRoute>} />
          <Route path="providers" element={<PermissionRoute permission="providers_access"><ProviderListPage /></PermissionRoute>} />
          <Route path="providers/new" element={<PermissionRoute permission="providers_access"><ProviderFormPage /></PermissionRoute>} />
          <Route path="providers/:id" element={<PermissionRoute permission="providers_access"><ProviderFormPage /></PermissionRoute>} />
          <Route path="agents" element={<PermissionRoute permission="agents_access"><AgentListPage /></PermissionRoute>} />
          <Route path="agents/new" element={<PermissionRoute permission="agents_access"><AgentFormPage /></PermissionRoute>} />
          <Route path="agents/:id" element={<PermissionRoute permission="agents_access"><AgentFormPage /></PermissionRoute>} />
          <Route
            path="users"
            element={(
              <RequirePermission permission="users_access" fallback={<Navigate to="/" replace />}>
                <UsersPage />
              </RequirePermission>
            )}
          />
          <Route
            path="users/new"
            element={(
              <RequirePermission permission="users_access" fallback={<Navigate to="/users" replace />}>
                <UserFormPage />
              </RequirePermission>
            )}
          />
          <Route
            path="users/:id"
            element={(
              <RequirePermission permission="users_access" fallback={<Navigate to="/users" replace />}>
                <UserFormPage />
              </RequirePermission>
            )}
          />
          <Route path="groups" element={<PermissionRoute permission="groups_access"><GroupsPage /></PermissionRoute>} />
          <Route path="groups/new" element={<PermissionRoute permission="groups_access"><GroupFormPage /></PermissionRoute>} />
          <Route path="groups/:id" element={<PermissionRoute permission="groups_access"><GroupFormPage /></PermissionRoute>} />
          <Route path="tenants" element={<PermissionRoute permission="tenants_access"><TenantsPage /></PermissionRoute>} />
          <Route path="tenants/new" element={<PermissionRoute permission="tenants_access"><TenantFormPage /></PermissionRoute>} />
          <Route path="tenants/:id" element={<PermissionRoute permission="tenants_access"><TenantFormPage /></PermissionRoute>} />
          <Route path="subscriptions/plans" element={<PermissionRoute permission="subscriptions_plans_access"><PlansPage /></PermissionRoute>} />
          <Route path="subscriptions/plans/new" element={<PermissionRoute permission="subscriptions_plans_access"><PlanFormPage /></PermissionRoute>} />
          <Route path="subscriptions/plans/:id" element={<PermissionRoute permission="subscriptions_plans_access"><PlanFormPage /></PermissionRoute>} />
          <Route path="subscriptions/addons" element={<PermissionRoute permission="subscriptions_addons_access"><AddonsPage /></PermissionRoute>} />
          <Route path="subscriptions/addons/new" element={<PermissionRoute permission="subscriptions_addons_access"><AddonFormPage /></PermissionRoute>} />
          <Route path="subscriptions/addons/:id" element={<PermissionRoute permission="subscriptions_addons_access"><AddonFormPage /></PermissionRoute>} />
          <Route path="subscriptions/minute-packs" element={<PermissionRoute permission="subscriptions_packs_access"><MinutePacksAdminPage /></PermissionRoute>} />
          <Route path="subscriptions/minute-packs/new" element={<PermissionRoute permission="subscriptions_packs_access"><MinutePackFormPage /></PermissionRoute>} />
          <Route path="subscriptions/minute-packs/:id" element={<PermissionRoute permission="subscriptions_packs_access"><MinutePackFormPage /></PermissionRoute>} />
          <Route path="transfer-groups" element={<PermissionRoute permission="transfer_groups_access"><TransferGroupsPage /></PermissionRoute>} />
          <Route path="transfer-groups/new" element={<PermissionRoute permission="transfer_groups_access"><TransferGroupFormPage /></PermissionRoute>} />
          <Route path="transfer-groups/:id" element={<PermissionRoute permission="transfer_groups_access"><TransferGroupFormPage /></PermissionRoute>} />
          <Route path="appointments" element={<PermissionRoute permission="appointments_access"><AppointmentCalendarListPage /></PermissionRoute>} />
          <Route path="appointments/new" element={<PermissionRoute permission="appointments_access"><AppointmentCalendarFormPage /></PermissionRoute>} />
          <Route path="appointments/:id" element={<PermissionRoute permission="appointments_access"><AppointmentCalendarFormPage /></PermissionRoute>} />
          <Route path="integrations" element={<PermissionRoute permission="integrations_access"><IntegrationsHubPage /></PermissionRoute>} />
          <Route path="integrations/contact-z" element={<PermissionRoute permission="contact_z_access"><ContactZPage /></PermissionRoute>} />
          <Route path="settings" element={<PermissionRoute permission="settings_access"><SettingsPage /></PermissionRoute>} />
          <Route path="account" element={<AccountPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="help" element={<PublicApiEntitlementRoute><HelpPage /></PublicApiEntitlementRoute>} />
          <Route path="help/*" element={<PublicApiEntitlementRoute><HelpArticlePage /></PublicApiEntitlementRoute>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
